import { Purchase } from 'src/dto/Credits';
import { GetAppsParams } from '../../dto/App';
import { baseApiWithCompanyId } from 'src/redux/services/baseApi';
import { format } from 'date-fns';

export interface PurchasesList {
  items: Purchase[];
  total: number;
  filters: GetAppsParams;
}

export type PurchaseCreatePayload = Omit<Purchase, 'id' | 'creation_date'>;
export type PurchaseUploadPayload = Purchase;

const mapDateTypes = (purchase: Purchase): Purchase => {
  purchase.creation_date = format(new Date(purchase.creation_date), 'yyyy-MM-dd HH:mm:ss');
  purchase.expiration_date = format(new Date(purchase.expiration_date), 'yyyy-MM-dd');
  purchase.activation_date = format(new Date(purchase.activation_date), 'yyyy-MM-dd');
  return purchase;
};

const purchasesApi = baseApiWithCompanyId.injectEndpoints({
  endpoints: (builder) => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    getPurchases: builder.query<PurchasesList, void>({
      query: () => ({
        url: '/v1/credits/report/purchase',
      }),
      providesTags: (result) =>
        result
          ? result.items.map((purchase) => ({
              type: 'Purchase',
              id: purchase.id,
            }))
          : ['Purchase'],
      transformResponse: (response: PurchasesList) => {
        response.items = response.items.map((item) => mapDateTypes(item));
        return response;
      },
    }),
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type
    deletePurchase: builder.mutation<void, Pick<Purchase, 'id'>>({
      query: ({ id }) => ({
        url: `/v1/credits/report/purchase/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (tagIgnored, resultTypeIgnored, args) => [{ type: 'Purchase', id: args.id }],
    }),
    updatePurchase: builder.mutation<Purchase, PurchaseUploadPayload>({
      query: (params) => ({
        url: `/v1/credits/report/purchase/${params.id}`,
        method: 'PUT',
        body: params,
      }),
      transformResponse: (response: Purchase) => mapDateTypes(response),
      invalidatesTags: (tagIgnored, resultTypeIgnored, args) => [{ type: 'Purchase', id: args.id }],
    }),
    // eslint-disable-next-line no-undef
    createPurchase: builder.mutation<ResponseType, PurchaseCreatePayload>({
      query: (params) => ({
        url: `/v1/credits/report/purchase/`,
        method: 'POST',
        body: params,
      }),
      invalidatesTags: () => ['Purchase'],
    }),
  }),
});

export const { useGetPurchasesQuery, useDeletePurchaseMutation, useUpdatePurchaseMutation, useCreatePurchaseMutation } = purchasesApi;
